import { Check, Loader2 } from "lucide-react";
import type { Product } from "@sakura-tech-lab/dashboard-api";

import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { getIngestionProgress } from "@/lib/ingestion-progress";
import { cn } from "@/lib/utils";
import { humanizeKey } from "./product-detail-utils";

interface ProductIngestionProgressProps {
  readonly product: Product;
}

/**
 * Per-stage ingestion stepper for the product detail page. Hidden once the
 * pipeline has finished (the summary panel's status badge covers that case).
 * Moderation is a parallel branch, so it is shown as a footer line rather than
 * a stage.
 */
export function ProductIngestionProgress({ product }: ProductIngestionProgressProps) {
  const progress = getIngestionProgress(product);

  if (!progress.isTracked || progress.isComplete) return null;

  const percent = progress.totalCount > 0
    ? Math.round((progress.completedCount / progress.totalCount) * 100)
    : 0;
  const moderationStatus = product.moderation?.status;

  return (
    <Card className="min-w-0">
      <CardHeader className="p-5 pb-3 sm:p-6 sm:pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-lg">Ingestion progress</CardTitle>
          {progress.isFailed ? (
            <Badge variant="destructive">Failed</Badge>
          ) : progress.isPartial ? (
            <Badge variant="outline" className="border-amber-300 text-amber-700">Partial</Badge>
          ) : (
            <Badge variant="secondary" className="tabular-nums">
              {progress.completedCount}/{progress.totalCount} stages
            </Badge>
          )}
        </div>
        <CardDescription>
          {progress.isFailed || progress.isPartial
            ? progress.error?.code === "NO_USABLE_IMAGES"
              ? "No usable product image could be indexed. Add a reachable image and ingest the product again."
              : progress.isPartial
                ? "Some selected processing stages failed, but usable product data is available."
                : "Processing failed. Re-run ingestion or contact support if it persists."
            : progress.currentStage
              ? `Currently: ${progress.currentStage.label}`
              : "Finishing up"}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 p-5 pt-0 sm:p-6 sm:pt-0">
        <Progress
          value={percent}
          className="h-1.5"
          indicatorClassName={cn(
            progress.isFailed ? "bg-red-500" : "bg-gradient-to-r from-pink-500 to-purple-500",
          )}
        />
        <ol className="space-y-2">
          {progress.stages.map((stage) => {
            const isCurrent = !progress.isFailed && !progress.isPartial && stage.key === progress.currentStage?.key;
            return (
              <li key={stage.key} className="flex items-center gap-2.5 text-sm">
                {stage.done ? (
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-950/60">
                    <Check className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
                  </span>
                ) : isCurrent ? (
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-pink-500 to-purple-500">
                    <Loader2 className="h-3 w-3 animate-spin text-white" />
                  </span>
                ) : (
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                    <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40" />
                  </span>
                )}
                <span
                  className={cn(
                    stage.done || isCurrent ? "text-foreground" : "text-muted-foreground",
                    isCurrent && "font-medium",
                  )}
                >
                  {stage.label}
                </span>
                {stage.skipped ? (
                  <span className="ml-auto text-xs text-muted-foreground">Skipped</span>
                ) : null}
              </li>
            );
          })}
        </ol>
        {product.moderation ? (
          <>
            <Separator />
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Moderation</span>
              <span>{moderationStatus ? humanizeKey(moderationStatus) : "Not started"}</span>
            </div>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}
